import { useLocation, useParams } from '@solidjs/router';
import { createEffect, createMemo, on, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { ErrorCatcher } from '../../tools/error-catcher';
import { PageCacheType } from '../../types/shared';
import { Link } from '../../ui-components/link/link';
import { NotFound } from '../404/404';
import { SeoSettings } from '../seo-settings/seo-settings';
import { ShareAndSave } from '../share-and-save/share-and-save';
import { VideoPostCard } from '../videos/video-post-card/video-post-card';
import { VideoData } from '../videos/videos-types';
import { GET_RELATED_VIDEOS_QUERY, GET_VIDEO_QUERY } from './fetch-video';
import { RelatedVideos } from './related-videos';
import { StyledBreadCrumbsWrapper, StyledCurrentPage, StyledLink, StyledVideoWrapper, VideoPostCardOuter } from './video.styles';
import { HCPWarning } from '../hcp-warning/hcp-warning';
import { navigateToAnchor } from '../../tools/scroll-to-element';

export const Video = () => {
    const params = useParams<{ videoSlug: string }>();
    const location = useLocation();

    const { 
        appStore,
        setCacheType,
        createCachedResource,
        setStatusCode,
        localize,
        viewport,
        videosEntityName,
        siteInfo,
        headerSettings,
    } = useContext(AppContext);

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;
    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    const showHcpHeader = () => headerSettings.activateHcpHeader && isHcp() && !isOriginalTablet();

    createEffect(
        on(
            () => location.hash,
            () => navigateToAnchor({
                id: location.hash.replace('#', ''),
                smallScreen: isMobile(),
                isCareSite: siteInfo.siteType === 'atos-care',
            })
        )
    );

    const [videoContent] = createCachedResource(
        GET_VIDEO_QUERY,
        () => ({
            key: `/video-item/${params.videoSlug}`,
        }),
        true
    );
    const videoData = createMemo((): VideoData => videoContent()?.video?.content);

    const tags = () => videoData()?.tags;
    const currentVideoId = () => videoData()?.id;

    const [relatedVideosContent] = createCachedResource(
        GET_RELATED_VIDEOS_QUERY,
        () => ({
            tags: tags(),
        }),
        true
    );

    const relatedVideosData = createMemo((): VideoData[] =>
        relatedVideosContent()
            ?.relatedvideos?.filter((video: any) => {
                return video.content.id !== currentVideoId();
            })
            .map((video: any) => video?.content)
    );

    const isHcp = () => {
        if (checkHcp()) {
            setCacheType(PageCacheType.none);
            return true;
        }
        setCacheType(PageCacheType.defaultCache);
        return false;
    };

    const checkHcp = () => {
        /**
         * "appStore.isHcp" behaves like a ratchet, once set to true, it can't be set to false,
         * unless the homepage is visited (which is a "patients" page, not a HCP page)
         */
        if (location.pathname === '/') {
            return false;
        }

        if (appStore.isHcp()) {
            return true;
        }
        
        return Boolean(videoData()?.isHealthcareProfessional);
    };

    const setHcp = () => {
        appStore.setHcp(checkHcp());
    };

    setHcp();

    createEffect(() => {
        setHcp();
    });

    const checkNotFound = () => {
        if (!videoData()) {
            // This only exists on SSR. We call it to set the right status from server.
            if (typeof setStatusCode === 'function') {
                setStatusCode(404);
            }
            return true;
        } else {
            if (typeof setStatusCode === 'function') {
                setStatusCode(200);
            }
            return false;
        }
    };

    const BreadCrumbsDesktop = () => (
        <>
            <StyledLink href={`/${videosEntityName}`}>{localize('instructional-videos', 'Instructional videos')}</StyledLink>/
            <StyledCurrentPage>{videoData()?.post_title}</StyledCurrentPage>
        </>
    );

    return (
        <ErrorCatcher componentName="Single video">
            <Show when={checkNotFound() && !videoContent.loading}>
                <NotFound />
            </Show>

            <StyledVideoWrapper adjustForHcpHeader={showHcpHeader()}>
                <Show when={videoData()}>
                    <HCPWarning isHcp={isHcp()} />
                    <SeoSettings seo={videoData().seo} title={videoData().post_title} />
                    <Section widthType="bgFull" templateShorthand={[12]} backgroundType="color" backgroundValue="white" customCss="z-index: 1;">
                        <StyledBreadCrumbsWrapper>
                            <Show when={viewport.width <= theme.breakpoints.MOBILE} fallback={BreadCrumbsDesktop()}>
                                <Link
                                    url={`/${videosEntityName}`}
                                    label={localize('instructional-videos', 'Instructional videos')}
                                    plainStyle={true}
                                    arrowPosition='left'
                                    arrowDirection={'left'}
                                    size={'small'}
                                />
                            </Show>
                        </StyledBreadCrumbsWrapper>
                    </Section>
                    <Section widthType="bgFull" templateShorthand={[12]}>
                        <VideoPostCardOuter>
                            <VideoPostCard
                                title={videoData()?.video_title}
                                videoUrl={videoData()?.video_link}
                                inGrouping={false}
                                isHealthcareProfessional={videoData()?.isHealthcareProfessional}
                                ableToPlay={true}
                                isVisibleForPublic={videoData()?.isVisibleForPublic}
                                permalink={videoData()?.permalink}
                                hideMyContentStar={true}
                            />
                        </VideoPostCardOuter>
                    </Section>

                    <Show when={relatedVideosData()?.length > 0}>
                        <RelatedVideos relatedVideos={relatedVideosData()} heading={localize('related-content', 'Related content')}></RelatedVideos>
                    </Show>

                    <ShareAndSave
                        type="video-item"
                        permalink={videoData()?.permalink}
                        title={videoData()?.video_title}
                        data={videoData()}
                    />

                </Show>
            </StyledVideoWrapper>
        </ErrorCatcher>
    );
};
